import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  Brain, TrendingUp, Shield, Zap, Activity, Heart,
  AlertTriangle, CheckCircle, Loader2, ChevronDown, ChevronRight, Info
} from 'lucide-react';

const STATE_COLORS = {
  optimal: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/15' },
  dysregulation: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/15' },
  depletion: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/15' },
  unknown: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-500/15' },
};

const CONFIDENCE_STYLE = {
  high: 'bg-emerald-500/15 text-emerald-400',
  medium: 'bg-amber-500/15 text-amber-400',
  low: 'bg-zinc-500/15 text-zinc-400',
};

const STABILITY_ICONS = {
  stable: { icon: CheckCircle, color: 'text-emerald-400', label: 'Stable' },
  fluctuating: { icon: Activity, color: 'text-amber-400', label: 'Fluctuating' },
  unstable: { icon: AlertTriangle, color: 'text-rose-400', label: 'Unstable' },
  unknown: { icon: Info, color: 'text-zinc-400', label: 'Awaiting Data' },
};

// ═══════════════════════════════════════════════════════════
// SUBSCRIBER CARD — "Your Current State"
// ═══════════════════════════════════════════════════════════

export function TwinFloSubscriberCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/twinflo/today`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Card className="bg-adm-surface border-white/[0.06]"><CardContent className="py-6 flex justify-center"><Loader2 className="w-4 h-4 text-adm-accent animate-spin" /></CardContent></Card>;
  if (!data || !data.current_state) return null;

  const cat = data.state_category || 'unknown';
  const colors = STATE_COLORS[cat] || STATE_COLORS.unknown;
  const stabInfo = STABILITY_ICONS[data.stability || 'unknown'];

  if (compact) {
    return (
      <Card className={`${colors.bg} border ${colors.border}`} data-testid="twinflo-compact">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className={`w-4 h-4 ${colors.text}`} />
              <span className={`text-sm font-['Outfit'] font-medium ${colors.text}`}>{data.current_state}</span>
              <Badge className={`${CONFIDENCE_STYLE[data.confidence_level || 'low']} text-[8px] border-0`}>{data.confidence_level}</Badge>
            </div>
            {data.best_action_today?.[0] && <span className="text-[10px] text-adm-tertiary truncate max-w-[200px]">{data.best_action_today[0]}</span>}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${colors.bg} border ${colors.border}`} data-testid="twinflo-subscriber-card">
      <CardContent className="py-4 px-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
              <Brain className={`w-4 h-4 ${colors.text}`} />
            </div>
            <div>
              <div className="text-[10px] text-adm-tertiary uppercase tracking-wider font-['Work_Sans']">Your Current State</div>
              <div className={`text-base font-['Outfit'] font-semibold ${colors.text}`}>{data.current_state}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${CONFIDENCE_STYLE[data.confidence_level || 'low']} text-[9px] border-0`}>{data.confidence_level}</Badge>
            {stabInfo && <stabInfo.icon className={`w-3.5 h-3.5 ${stabInfo.color}`} title={stabInfo.label} />}
          </div>
        </div>

        {/* Explanation */}
        <p className="text-xs text-adm-secondary leading-relaxed">{data.state_explanation}</p>

        {/* Why */}
        {data.why_this_is_happening?.length > 0 && (
          <div>
            <div className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-1.5">Why this is happening</div>
            <div className="space-y-1">
              {data.why_this_is_happening.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-adm-secondary">
                  <ChevronRight className="w-3 h-3 mt-0.5 text-adm-tertiary flex-shrink-0" />
                  {w}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {data.best_action_today?.length > 0 && (
          <div className="bg-white/[0.03] rounded-lg p-3">
            <div className="text-[10px] text-adm-accent uppercase tracking-wider mb-1.5">What to do today</div>
            <div className="space-y-1">
              {data.best_action_today.map((a, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-adm-primary">
                  <Zap className="w-3 h-3 mt-0.5 text-adm-accent flex-shrink-0" />
                  {a}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signal note */}
        <div className="text-[10px] text-adm-tertiary italic">{data.signal_message}</div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// STAFF PANEL — TwinFlo Overview for a Subscriber
// ═══════════════════════════════════════════════════════════

const SCORE_META = {
  regulation_score: { label: 'Regulation', icon: Shield, good: 'high' },
  emotional_load_score: { label: 'Emotional Load', icon: Heart, good: 'low' },
  reactivity_score: { label: 'Reactivity', icon: Zap, good: 'low' },
  cognitive_strain_score: { label: 'Cognitive Strain', icon: Brain, good: 'low' },
  resilience_score: { label: 'Resilience', icon: TrendingUp, good: 'high' },
  alignment_score: { label: 'Alignment', icon: Activity, good: 'high' },
};

export function TwinFloStaffPanel({ subscriberId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/twinflo/staff/${subscriberId}/overview`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [subscriberId]);

  if (loading) return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 text-adm-accent animate-spin" /></div>;
  if (!data || data.error || !data.state) return null;

  const dom = data.state?.dominant_state || 'unknown';
  const meta = { calm_alignment: 'optimal', performative_flow: 'optimal', recovery_debt: 'depletion', emotional_fatigue: 'depletion' };
  const cat = meta[dom] || 'dysregulation';
  const colors = STATE_COLORS[cat] || STATE_COLORS.unknown;
  const stateLabel = dom.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const stabInfo = STABILITY_ICONS[data.stability || 'unknown'];
  const scores = data.scores || {};

  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${colors.bg}`} data-testid="twinflo-staff-compact">
        <Brain className={`w-3 h-3 ${colors.text}`} />
        <span className={`text-[10px] font-medium ${colors.text}`}>{stateLabel}</span>
        <span className="text-[9px] text-adm-tertiary">Q:{Math.round((data.signal_quality || 0) * 100)}%</span>
        {data.priority_flags?.length > 0 && <AlertTriangle className="w-3 h-3 text-rose-400" />}
      </div>
    );
  }

  return (
    <Card className="bg-adm-surface border-white/[0.06]" data-testid="twinflo-staff-panel">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <button className="w-full flex items-center justify-between" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg ${colors.bg} flex items-center justify-center`}>
              <Brain className={`w-3.5 h-3.5 ${colors.text}`} />
            </div>
            <div className="text-left">
              <div className="text-[10px] text-adm-tertiary">TwinFlo State</div>
              <div className={`text-sm font-['Outfit'] font-medium ${colors.text}`}>{stateLabel}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${CONFIDENCE_STYLE[data.confidence_level || 'low']} text-[8px] border-0`}>{data.confidence_level}</Badge>
            {stabInfo && <Badge className={`text-[8px] border-0 bg-white/5 ${stabInfo.color}`}>{stabInfo.label}</Badge>}
            {expanded ? <ChevronDown className="w-3.5 h-3.5 text-adm-tertiary" /> : <ChevronRight className="w-3.5 h-3.5 text-adm-tertiary" />}
          </div>
        </button>

        {/* Priority Flags */}
        {data.priority_flags?.length > 0 && (
          <div className="space-y-1">
            {data.priority_flags.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[10px] text-rose-400 bg-rose-500/5 rounded px-2 py-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {f}
              </div>
            ))}
          </div>
        )}

        {/* Scores Grid */}
        <div className="grid grid-cols-3 gap-1.5">
          {Object.entries(SCORE_META).map(([key, m]) => {
            const val = scores[key] ?? 0;
            const isGood = m.good === 'high' ? val >= 60 : val <= 40;
            const isBad = m.good === 'high' ? val < 30 : val > 60;
            const barColor = isBad ? 'bg-rose-500' : isGood ? 'bg-emerald-500' : 'bg-amber-500';
            return (
              <div key={key} className="bg-white/[0.02] rounded-lg p-2" title={data.tooltips?.[key.replace('_score', '')] || ''}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] text-adm-tertiary">{m.label}</span>
                  <span className={`text-xs font-semibold ${isBad ? 'text-rose-400' : isGood ? 'text-emerald-400' : 'text-amber-400'}`}>{val}</span>
                </div>
                <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${barColor}`} style={{ width: `${val}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {expanded && (
          <>
            {/* Clinical Summary */}
            <div className="bg-white/[0.02] rounded-lg p-3">
              <div className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-1">Clinical Summary</div>
              <p className="text-xs text-adm-secondary">{data.clinical_summary}</p>
            </div>

            {/* Drivers */}
            {data.drivers?.primary_driver_chain?.length > 0 && (
              <div>
                <div className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-1.5">Primary Drivers</div>
                <div className="flex flex-wrap gap-1">
                  {data.drivers.primary_driver_chain.map((d, i) => (
                    <Badge key={i} className="text-[8px] bg-white/[0.04] text-adm-secondary border-0">{d.replace(':', ': ')}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Signal Quality */}
            <div className="flex items-center gap-3 text-[10px] text-adm-tertiary">
              <span>Signal: {Math.round((data.signal_quality || 0) * 100)}%</span>
              <span>Direction: <span className={scores.risk_direction === 'improving' ? 'text-emerald-400' : scores.risk_direction === 'worsening' ? 'text-rose-400' : 'text-adm-secondary'}>{scores.risk_direction || 'stable'}</span></span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// OPS LIST COMPACT — for Ops Cockpit subscriber cards
// ═══════════════════════════════════════════════════════════

export function TwinFloOpsCompact({ subscriberId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/twinflo/staff/${subscriberId}/overview`)
      .then(r => setData(r.data)).catch(() => {});
  }, [subscriberId]);

  if (!data || data.error || !data.state) return null;

  const dom = data.state?.dominant_state || 'unknown';
  const meta = { calm_alignment: 'optimal', performative_flow: 'optimal', recovery_debt: 'depletion', emotional_fatigue: 'depletion' };
  const cat = meta[dom] || 'dysregulation';
  const colors = STATE_COLORS[cat] || STATE_COLORS.unknown;
  const label = dom.replace(/_/g, ' ').split(' ').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');

  return (
    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${colors.bg}`} data-testid="twinflo-ops-badge">
      <Brain className={`w-2.5 h-2.5 ${colors.text}`} />
      <span className={`text-[8px] font-medium ${colors.text}`}>{label}</span>
    </div>
  );
}
